import { observer } from "mobx-react-lite";
import React from "react";
import { XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import type PlanModel from "../../../plans/PlanModel";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { Title } from "../../../ui/Title";
import { UserAvatarGroupRaw } from "../../../ui/UserAvatarGroupRaw";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../BusinessPlanModel";

export const BusinessPlaniraJoinRow = observer(
  ({
    plan,
    businessPlan,
  }: {
    plan: PlanModel;
    businessPlan: BusinessPlanModel;
  }) => {
    const user = businessPlan.userInPlan;

    if (!user) {
      return null;
    }

    const isApplying = plan.data.joinOptions.waitingForApproval?.some(
      (u) => u === user.uid,
    );

    const showOwnTitle = plan.data.title !== businessPlan.title;

    const users: BusinessStaticUserInfoWithUserUid[] = [];

    const displayNames: string[] = [];
    plan.data.userIds.forEach((userUid) => {
      const info = businessPlan.data.userInfo[userUid];
      users.push({ userUid, ...info });
      displayNames.push(info.displayName);
    });

    return (
      <YStack gap={"$medium"}>
        <XStack
          key={plan.uid}
          justifyContent="space-between"
          alignItems="center"
          width={"100%"}
        >
          <YStack gap={"$small"}>
            {showOwnTitle && <Label>{plan.data.title}</Label>}
            <UserAvatarGroupRaw
              businessPlan={plan.businessPlan}
              users={users}
              skipPadding
            />
            <Body>{displayNames.join(", ")}</Body>
          </YStack>

          <XStack gap={"$medium"}>
            {!isApplying && (
              <Button
                type={"primary"}
                onPress={() => {
                  plan.addUserToApprovalList(user?.uid);
                }}
              >
                Apply
              </Button>
            )}
            {isApplying && (
              <Button
                type={"secondary"}
                onPress={() => {
                  plan.removeUserFromApprovalList();
                }}
              >
                Stop
              </Button>
            )}
          </XStack>
        </XStack>
        {isApplying && (
          <Body muted>
            You are applying - let someone in your team know so they can let you
            in.
          </Body>
        )}
      </YStack>
    );
  },
);

export const BusinessPlanirasList: React.FC<{
  businessPlan: BusinessPlanModel;
}> = observer(({ businessPlan }) => {
  const { plansStore } = useStores();
  const { plansApi } = useApi();
  const planiras = businessPlan.planiras;

  const user = businessPlan.userInPlan;

  // Load planiras
  React.useEffect(() => {
    businessPlan.planIds.forEach((planId) => {
      if (!plansStore.byId.get(planId)) {
        plansApi.getById(planId);
      }
    });
  }, [businessPlan, plansStore, plansApi]);

  if (!planiras.length || !user) {
    return null;
  }

  return (
    <Box flex={1} borderBottomColor={"$divider"} borderBottomWidth={1}>
      <YStack gap={"$medium"}>
        <Title>All Teams</Title>
        <Group>
          {planiras.map((plan) => (
            <BusinessPlaniraJoinRow
              key={plan.uid}
              plan={plan}
              businessPlan={businessPlan}
            />
          ))}
        </Group>
      </YStack>
    </Box>
  );
});
